import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiFetch } from '../lib/auth';
import {
  Plus, Search, Edit2, Archive, Eye, Loader2, Table, List,
  History, RotateCcw, Sparkles, Check, X, AlertCircle, Trash2,
  Image as ImageIcon, Star, Layers, RefreshCw, UploadCloud, CheckCircle2,
  Share2, ExternalLink
} from 'lucide-react';
import type { Product, Category, MediaAsset, Variant } from '@zarin/types';

interface AuditLogItem {
  id: string;
  productId: string;
  staffId: string | null;
  action: string;
  diff: Record<string, unknown>;
  createdAt: string;
}

interface BulkEditRow {
  productId: string;
  variantId: string;
  title: string;
  handle: string;
  status: 'active' | 'draft' | 'archived';
  priceRupees: number;
  onHand: number;
  tags: string;
  isDirty?: boolean;
}
import { optimizeImageForUpload } from '../lib/imageUtils';

const ADMIN_COLOR_MAP: Record<string, string> = {
  black: '#141416',
  white: '#FFFFFF',
  ivory: '#FFFFF0',
  cream: '#FFFDD0',
  beige: '#F5F5DC',
  brown: '#4A3728',
  blue: '#1E3A8A',
  'navy blue': '#0A192F',
  navy: '#0A192F',
  'sky blue': '#38BDF8',
  red: '#991B1B',
  maroon: '#581C87',
  burgundy: '#581C87',
  green: '#166534',
  emerald: '#065F46',
  olive: '#556B2F',
  gold: '#C9A84C',
  yellow: '#EAB308',
  pink: '#DB2777',
  rose: '#FB7185',
  orange: '#C2410C',
  rust: '#9A3412',
  grey: '#4B5563',
  gray: '#4B5563',
  charcoal: '#1F2937',
  tan: '#D2B48C',
  camel: '#C19A6B',
  copper: '#B87333',
  bronze: '#CD7F32',
  silver: '#E5E7EB',
  lavender: '#E6E6FA',
  purple: '#6B21A8',
  teal: '#0D9488',
  cyan: '#06B6D4',
};

function getAdminColorHex(colorName?: string): string {
  if (!colorName) return '#444';
  const norm = colorName.trim().toLowerCase();
  if (ADMIN_COLOR_MAP[norm]) return ADMIN_COLOR_MAP[norm];
  for (const [key, hex] of Object.entries(ADMIN_COLOR_MAP)) {
    if (norm.includes(key)) return hex;
  }
  return '#444';
}

export default function ProductsPage() {
  const [products, setProducts]       = useState<Product[]>([]);
  const [loading,  setLoading]        = useState(true);
  const [search,   setSearch]         = useState('');
  const [status,   setStatus]         = useState<string>('');
  const [cursor,   setCursor]         = useState<string | null>(null);
  const [hasMore,  setHasMore]        = useState(false);
  const [viewMode, setViewMode]       = useState<'list' | 'bulk_grid'>('list');

  // Bulk edit grid state
  const [bulkRows, setBulkRows]       = useState<BulkEditRow[]>([]);
  const [savingBulk, setSavingBulk]   = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState<string | null>(null);

  // History & Restore Modal
  const [historyModalProd, setHistoryModalProd] = useState<Product | null>(null);
  const [auditLogs, setAuditLogs]               = useState<AuditLogItem[]>([]);
  const [loadingHistory, setLoadingHistory]     = useState(false);
  const [restoringId, setRestoringId]           = useState<string | null>(null);

  // Curate Recommendations Modal
  const [recModalProd, setRecModalProd]         = useState<Product | null>(null);
  const [selectedRecIds, setSelectedRecIds]     = useState<string[]>([]);
  const [savingRec, setSavingRec]               = useState(false);

  // Create Garment Modal
  const [showAddModal, setShowAddModal]         = useState(false);
  const [newTitle, setNewTitle]                 = useState('');
  const [newHandle, setNewHandle]               = useState('');
  const [newDesc, setNewDesc]                   = useState('');
  const [newPriceRupees, setNewPriceRupees]     = useState('4500');
  const [newStock, setNewStock]                 = useState('10');
  const [newTags, setNewTags]                   = useState('');
  const [newProductType, setNewProductType]     = useState('Tops');
  const [newCategoryId, setNewCategoryId]       = useState('');
  const [categories, setCategories]             = useState<Category[]>([]);
  const [creatingProduct, setCreatingProduct]   = useState(false);
  const [newImageUrls, setNewImageUrls]         = useState<string[]>([]);
  const [newImageUrlInput, setNewImageUrlInput] = useState('');
  const [newColorName, setNewColorName]         = useState('');
  const [variantMode, setVariantMode]           = useState<'single' | 'sizes'>('single');
  const [multiSizes, setMultiSizes]             = useState<Array<{ size: string; stock: number }>>([
    { size: 'XS', stock: 5 },
    { size: 'S',  stock: 10 },
    { size: 'M',  stock: 15 },
    { size: 'L',  stock: 10 },
    { size: 'XL', stock: 5 },
  ]);

  // Edit Garment Modal
  const [editingProd, setEditingProd]           = useState<Product | null>(null);
  const [editTitle, setEditTitle]               = useState('');
  const [editHandle, setEditHandle]             = useState('');
  const [editDesc, setEditDesc]                 = useState('');
  const [editCategoryId, setEditCategoryId]     = useState('');
  const [editProductType, setEditProductType]   = useState('');
  const [editTags, setEditTags]                 = useState('');
  const [editStatus, setEditStatus]             = useState<'active' | 'draft' | 'archived'>('active');
  const [savingEdit, setSavingEdit]             = useState(false);
  const [editTab, setEditTab]                   = useState<'details' | 'media' | 'variants'>('details');
  const [editImageUrl, setEditImageUrl]         = useState('');
  const [addingImage, setAddingImage]           = useState(false);
  const [newVariantTitle, setNewVariantTitle]   = useState('');
  const [newVariantColor, setNewVariantColor]   = useState('');
  const [newVariantSize, setNewVariantSize]     = useState('Free Size');
  const [newVariantSku, setNewVariantSku]       = useState('');
  const [newVariantPrice, setNewVariantPrice]   = useState('4500');
  const [newVariantStock, setNewVariantStock]   = useState('10');
  const [addingVariant, setAddingVariant]       = useState(false);
  const [savingVariantId, setSavingVariantId]   = useState<string | null>(null);
  const [deletingVariantId, setDeletingVariantId] = useState<string | null>(null);

  // Media Library Picker Modal
  const [showMediaPicker, setShowMediaPicker]   = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState<'add' | 'edit'>('add');
  const [mediaAssets, setMediaAssets]           = useState<MediaAsset[]>([]);
  const [loadingMedia, setLoadingMedia]         = useState(false);

  // File Upload State & Native Input Refs
  const addFileInputRef     = useRef<HTMLInputElement>(null);
  const editFileInputRef    = useRef<HTMLInputElement>(null);
  const pickerFileInputRef  = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage]         = useState(false);
  const [uploadingTarget, setUploadingTarget]       = useState<'add' | 'edit' | 'picker' | null>(null);
  const [uploadProgressText, setUploadProgressText] = useState<string | null>(null);

  const load = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', sort: 'updated_at_desc' });
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (!reset && cursor) params.set('cursor', cursor);

      const res = await apiFetch<{ data: Product[]; meta: { cursor: string | null; hasMore: boolean } }>(`/products?${params}`);
      const nextProducts = reset ? res.data : [...products, ...res.data];
      setProducts(nextProducts);
      setCursor(res.meta.cursor);
      setHasMore(res.meta.hasMore);

      // Initialize bulk edit rows
      const rows: BulkEditRow[] = [];
      for (const p of nextProducts) {
        for (const v of p.variants) {
          rows.push({
            productId:   p.id,
            variantId:   v.id,
            title:       `${p.title} (${v.title})`,
            handle:      p.handle,
            status:      p.status as 'active' | 'draft' | 'archived',
            priceRupees: Math.round(v.priceMinor / 100),
            onHand:      v.onHand ?? 0,
            tags:        p.tags.join(', '),
            isDirty:     false,
          });
        }
      }
      setBulkRows(rows);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, status, cursor]);

  useEffect(() => { load(true); }, [search, status]);

  useEffect(() => {
    apiFetch<{ data: Category[] }>('/categories').then(res => setCategories(res.data || [])).catch(() => {});
  }, []);

  const handleBulkChange = (index: number, field: keyof BulkEditRow, val: any) => {
    setBulkRows(prev => {
      const next = [...prev];
      const row = { ...next[index]!, [field]: val, isDirty: true };
      next[index] = row;
      return next;
    });
  };

  const handleSaveBulk = async () => {
    const dirty = bulkRows.filter(r => r.isDirty);
    if (dirty.length === 0) return;

    setSavingBulk(true);
    setBulkSuccess(null);
    try {
      // Group by productId
      const grouped = new Map<string, {
        productId: string;
        status?: 'active' | 'draft' | 'archived';
        tags?: string[];
        variants: Array<{ variantId: string; priceMinor: number; onHand: number }>;
      }>();

      for (const row of dirty) {
        if (!grouped.has(row.productId)) {
          grouped.set(row.productId, {
            productId: row.productId,
            status:    row.status,
            tags:      row.tags.split(',').map(t => t.trim()).filter(Boolean),
            variants:  [],
          });
        }
        grouped.get(row.productId)!.variants.push({
          variantId:  row.variantId,
          priceMinor: Math.round(row.priceRupees * 100),
          onHand:     Number(row.onHand),
        });
      }

      await apiFetch('/products/bulk-update', {
        method: 'POST',
        body: JSON.stringify({ updates: Array.from(grouped.values()) }),
      });

      setBulkSuccess(`Successfully saved ${dirty.length} variant updates to ledger!`);
      setTimeout(() => setBulkSuccess(null), 4000);
      load(true);
    } catch (err) {
      console.error('Failed to bulk save:', err);
    } finally {
      setSavingBulk(false);
    }
  };

  const openHistory = async (prod: Product) => {
    setHistoryModalProd(prod);
    setLoadingHistory(true);
    try {
      const res = await apiFetch<{ data: AuditLogItem[] }>(`/products/${prod.id}/history`);
      setAuditLogs(res.data);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const restoreSnapshot = async (logId: string) => {
    if (!historyModalProd) return;
    setRestoringId(logId);
    try {
      await apiFetch(`/products/${historyModalProd.id}/restore`, {
        method: 'POST',
        body: JSON.stringify({ logId }),
      });
      alert('Snapshot restored successfully!');
      setHistoryModalProd(null);
      load(true);
    } catch (err) {
      console.error('Restore failed:', err);
    } finally {
      setRestoringId(null);
    }
  };

  const openRecsModal = async (prod: Product) => {
    setRecModalProd(prod);
    try {
      const res = await apiFetch<{ data: { completeTheLook: Array<{ id: string }> } }>(`/recommendations/${prod.id}`);
      setSelectedRecIds(res.data.completeTheLook.map(c => c.id));
    } catch {
      setSelectedRecIds([]);
    }
  };

  const saveRecommendations = async () => {
    if (!recModalProd) return;
    setSavingRec(true);
    try {
      for (const recId of selectedRecIds) {
        await apiFetch(`/recommendations/${recModalProd.id}`, {
          method: 'POST',
          body: JSON.stringify({ recommendedProductId: recId, type: 'complete_the_look' }),
        });
      }
      setRecModalProd(null);
      alert('Curated recommendations saved for storefront!');
    } catch (err) {
      console.error('Rec save error:', err);
    } finally {
      setSavingRec(false);
    }
  };

  const getStorefrontProductUrl = (handle: string): string => {
    if (typeof window !== 'undefined') {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return `http://localhost:4321/products/${handle}`;
      }
    }
    return `https://zarin-storefront.pages.dev/products/${handle}`;
  };

  const handleShareProductLink = async (p: Product) => {
    const url = getStorefrontProductUrl(p.handle);
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        alert(`Product link copied to clipboard:\n${url}`);
        return;
      }
    } catch {}
    prompt('Copy shareable product link:', url);
  };

  const openMediaPicker = async (target: 'add' | 'edit') => {
    setMediaPickerTarget(target);
    setShowMediaPicker(true);
    if (mediaAssets.length === 0) {
      setLoadingMedia(true);
      try {
        const res = await apiFetch<{ data: MediaAsset[] }>('/media');
        setMediaAssets(res.data || []);
      } catch (err) {
        console.error('Failed to load media library:', err);
      } finally {
        setLoadingMedia(false);
      }
    }
  };

  const handleSelectMediaAsset = (asset: MediaAsset) => {
    if (mediaPickerTarget === 'add') {
      setNewImageUrls(prev => prev.includes(asset.url) ? prev : [...prev, asset.url]);
    } else if (mediaPickerTarget === 'edit') {
      handleAttachImage(asset.url);
    }
    setShowMediaPicker(false);
  };

  // Infallible Upload Engine (Client Optimization + Cloudflare D1 Native Media & Cloudinary)
  const uploadFileToCloudinary = async (file: File): Promise<string> => {
    // 1. Optimize image client-side to ensure rapid transfer & eliminate payload limits
    const optimizedFile = await optimizeImageForUpload(file).catch(() => file);

    try {
      const formData = new FormData();
      formData.append('file', optimizedFile);
      formData.append('folder', 'zarin-wear/images');

      const res = await apiFetch<{ data: { url: string } }>('/media/cloudinary/upload', {
        method: 'POST',
        body: formData,
      });
      if (res?.data?.url) return res.data.url;
    } catch (backendErr) {
      console.warn('Backend media upload error, attempting edge fallback:', backendErr);
    }

    // 2. Direct fallback via registration or base64 data-url to guarantee the merchant's workflow never breaks
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result as string;
        try {
          const directAsset = await apiFetch<{ data: MediaAsset }>('/media', {
            method: 'POST',
            body: JSON.stringify({
              key: `med_local_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
              url: dataUrl,
              altText: file.name.replace(/\.[^/.]+$/, ''),
              mimeType: optimizedFile.type || 'image/jpeg',
              sizeBytes: optimizedFile.size,
              resourceType: 'image',
              format: 'jpg',
            }),
          });
          if (directAsset?.data?.url) return resolve(directAsset.data.url);
        } catch {}
        // Fallback directly to dataUrl so the photo is safely attached
        resolve(dataUrl);
      };
      reader.onerror = () => reject(new Error('Failed to read image file.'));
      reader.readAsDataURL(optimizedFile);
    });
  };

  const handleUploadForAdd = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    setUploadingImage(true);
    setUploadingTarget('add');
    try {
      const uploaded: string[] = [];
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i]!;
        setUploadProgressText(`Uploading ${i + 1} of ${fileArray.length}: ${file.name}…`);
        const url = await uploadFileToCloudinary(file);
        if (url) uploaded.push(url);
      }
      if (uploaded.length > 0) {
        setNewImageUrls(prev => [...prev, ...uploaded]);
      }
    } catch (err: any) {
      alert('Upload failed: ' + (err.message || String(err)));
    } finally {
      setUploadingImage(false);
      setUploadingTarget(null);
      setUploadProgressText(null);
      if (addFileInputRef.current) addFileInputRef.current.value = '';
    }
  };

  const handleUploadForEdit = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0 || !editingProd) return;
    const fileArray = Array.from(files);
    setUploadingImage(true);
    setUploadingTarget('edit');
    try {
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i]!;
        setUploadProgressText(`Uploading ${i + 1} of ${fileArray.length}: ${file.name}…`);
        const url = await uploadFileToCloudinary(file);
        const shouldBeFeatured = (!editingProd.images || editingProd.images.length === 0) && i === 0;
        await handleAttachImage(url, shouldBeFeatured);
      }
    } catch (err: any) {
      alert('Upload failed: ' + (err.message || String(err)));
    } finally {
      setUploadingImage(false);
      setUploadingTarget(null);
      setUploadProgressText(null);
      if (editFileInputRef.current) editFileInputRef.current.value = '';
    }
  };

  const handleUploadForPicker = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const fileArray = Array.from(files);
    setUploadingImage(true);
    setUploadingTarget('picker');
    try {
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i]!;
        setUploadProgressText(`Uploading ${i + 1} of ${fileArray.length}: ${file.name}…`);
        const url = await uploadFileToCloudinary(file);
        if (mediaPickerTarget === 'add') {
          setNewImageUrls(prev => prev.includes(url) ? prev : [...prev, url]);
        } else if (mediaPickerTarget === 'edit') {
          await handleAttachImage(url, (!editingProd?.images || editingProd.images.length === 0) && i === 0);
        }
      }
      setShowMediaPicker(false);
      apiFetch<{ data: MediaAsset[] }>('/media').then(r => setMediaAssets(r.data || [])).catch(() => {});
    } catch (err: any) {
      alert('Upload failed: ' + (err.message || String(err)));
    } finally {
      setUploadingImage(false);
      setUploadingTarget(null);
      setUploadProgressText(null);
      if (pickerFileInputRef.current) pickerFileInputRef.current.value = '';
    }
  };

  const handleCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanTitle = newTitle.trim();
    if (!cleanTitle) return;

    const priceRupees = parseFloat(newPriceRupees) || 0;
    const stockUnits = parseInt(newStock, 10) || 0;
    const pendingUrls = newImageUrlInput
      .split(/[\n,]+/)
      .map(u => u.trim())
      .filter(Boolean);
    const allImages = [...newImageUrls, ...pendingUrls];

    setCreatingProduct(true);
    try {
      // 1. Create product with initial images if provided
      const res = await apiFetch<{ data: Product }>('/products', {
        method: 'POST',
        body: JSON.stringify({
          title: cleanTitle,
          handle: newHandle.trim() || undefined,
          descriptionHtml: `<p>${newDesc.trim()}</p>`,
          status: 'active',
          productType: newProductType || undefined,
          categoryId: newCategoryId || undefined,
          tags: newTags.split(',').map(t => t.trim()).filter(Boolean),
          images: allImages.length > 0 ? allImages : undefined,
        }),
      });

      // 2. Create variants with multi-color & multi-size support
      const productId = res?.data?.id;
      if (productId) {
        const skuPrefix = (newHandle.trim() || cleanTitle).replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase() || 'PROD';
        const rawColors = newColorName
          .split(/[,/+|&]| and /i)
          .map(c => c.trim())
          .filter(Boolean);

        if (rawColors.length > 0) {
          // One or more distinct colors specified
          for (const col of rawColors) {
            const colPrefix = col.replace(/[^a-zA-Z0-9]/g, '').slice(0, 3).toUpperCase() || 'COL';
            if (variantMode === 'sizes') {
              for (const s of multiSizes) {
                const uniqueSku = `ZAR-${skuPrefix}-${colPrefix}-${s.size}-${Math.floor(1000 + Math.random() * 9000)}`;
                await apiFetch(`/products/${productId}/variants`, {
                  method: 'POST',
                  body: JSON.stringify({
                    title: `${col} / ${s.size}`,
                    priceMinor: Math.round(priceRupees * 100),
                    onHand: Number(s.stock) || 0,
                    sku: uniqueSku,
                    optionValues: { Color: col, Size: s.size },
                  }),
                });
              }
            } else {
              const uniqueSku = `ZAR-${skuPrefix}-${colPrefix}-${Math.floor(1000 + Math.random() * 9000)}`;
              const distributedStock = Math.max(1, Math.floor(stockUnits / rawColors.length));
              await apiFetch(`/products/${productId}/variants`, {
                method: 'POST',
                body: JSON.stringify({
                  title: `${col} / Free Size`,
                  priceMinor: Math.round(priceRupees * 100),
                  onHand: distributedStock,
                  sku: uniqueSku,
                  optionValues: { Color: col, Size: 'Free Size' },
                }),
              });
            }
          }
        } else {
          // No color specified, create size-only or standard variant
          if (variantMode === 'sizes') {
            for (const s of multiSizes) {
              const uniqueSku = `ZAR-${skuPrefix}-${s.size}-${Math.floor(1000 + Math.random() * 9000)}`;
              await apiFetch(`/products/${productId}/variants`, {
                method: 'POST',
                body: JSON.stringify({
                  title: `Size ${s.size}`,
                  priceMinor: Math.round(priceRupees * 100),
                  onHand: Number(s.stock) || 0,
                  sku: uniqueSku,
                  optionValues: { Size: s.size },
                }),
              });
            }
          } else {
            const uniqueSku = `ZAR-${skuPrefix}-${Math.floor(1000 + Math.random() * 9000)}`;
            await apiFetch(`/products/${productId}/variants`, {
              method: 'POST',
              body: JSON.stringify({
                title: 'Standard Edition',
                priceMinor: Math.round(priceRupees * 100),
                onHand: stockUnits,
                sku: uniqueSku,
                optionValues: { Size: 'Free Size' },
              }),
            });
          }
        }
      }

      setShowAddModal(false);
      setNewTitle('');
      setNewHandle('');
      setNewDesc('');
      setNewCategoryId('');
      setNewPriceRupees('4500');
      setNewStock('10');
      setNewTags('');
      setNewImageUrls([]);
      setNewImageUrlInput('');
      setNewColorName('');
      setVariantMode('single');
      load(true);
    } catch (err: any) {
      console.error('Failed to create garment:', err);
      alert('Failed to create garment: ' + (err?.detail || err?.title || err?.message || String(err)));
    } finally {
      setCreatingProduct(false);
    }
  };

  const handleDeleteProduct = async (prod: Product) => {
    if (!confirm(`Are you sure you want to delete "${prod.title}"? This cannot be undone.`)) return;
    try {
      await apiFetch(`/products/${prod.id}`, { method: 'DELETE' });
      load(true);
    } catch (err: any) {
      console.error('Failed to delete product:', err);
      alert('Failed to delete product: ' + (err?.detail || err?.title || err?.message || String(err)));
    }
  };

  const handleToggleArchive = async (prod: Product) => {
    const nextStatus = prod.status === 'archived' ? 'active' : 'archived';
    try {
      await apiFetch(`/products/${prod.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      });
      load(true);
    } catch (err: any) {
      console.error('Failed to update product status:', err);
      alert('Failed to update product status: ' + (err?.detail || err?.title || err?.message || String(err)));
    }
  };

  const handleOpenEdit = (prod: Product) => {
    setEditingProd(prod);
    setEditTitle(prod.title);
    setEditHandle(prod.handle);
    setEditDesc(prod.descriptionHtml.replace(/^<p>|<\/p>$/g, ''));
    setEditCategoryId(prod.categoryId || '');
    setEditProductType(prod.productType || '');
    setEditTags((prod.tags || []).join(', '));
    setEditStatus(prod.status);
    setEditTab('details');
    setEditImageUrl('');
    setNewVariantTitle('');
    setNewVariantColor('');
    setNewVariantSize('Free Size');
    const skuPrefix = prod.handle.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase() || 'PROD';
    setNewVariantSku(`ZAR-${skuPrefix}-${Math.floor(1000 + Math.random() * 9000)}`);
    setNewVariantPrice(prod.variants[0] ? String(Math.round(prod.variants[0].priceMinor / 100)) : '4500');
    setNewVariantStock('10');
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProd) return;
    setSavingEdit(true);
    try {
      await apiFetch(`/products/${editingProd.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: editTitle.trim(),
          handle: editHandle.trim() || undefined,
          descriptionHtml: `<p>${editDesc.trim()}</p>`,
          status: editStatus,
          productType: editProductType.trim() || undefined,
          categoryId: editCategoryId || null,
          tags: editTags.split(',').map(t => t.trim()).filter(Boolean),
        }),
      });
      setEditingProd(null);
      load(true);
    } catch (err: any) {
      console.error('Failed to save product changes:', err);
      alert('Failed to save product changes: ' + (err?.detail || err?.title || err?.message || String(err)));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAttachImage = async (url: string, isFeatured = false) => {
    if (!editingProd || !url.trim()) return;
    setAddingImage(true);
    try {
      const urls = url.split(/[\n,]+/).map(u => u.trim()).filter(Boolean);
      for (let i = 0; i < urls.length; i++) {
        const u = urls[i]!;
        await apiFetch(`/products/${editingProd.id}/media`, {
          method: 'POST',
          body: JSON.stringify({ url: u, isFeatured: isFeatured && i === 0 }),
        });
      }
      const refreshed = await apiFetch<{ data: Product }>(`/products/${editingProd.id}`);
      if (refreshed?.data) setEditingProd(refreshed.data);
      setEditImageUrl('');
      load(false);
    } catch (err: any) {
      alert('Failed to attach image: ' + (err?.detail || err?.title || err?.message || String(err)));
    } finally {
      setAddingImage(false);
    }
  };

  const handleSetFeaturedImage = async (mediaId: string) => {
    if (!editingProd) return;
    try {
      await apiFetch(`/products/${editingProd.id}/media`, {
        method: 'POST',
        body: JSON.stringify({ mediaId, isFeatured: true }),
      });
      const refreshed = await apiFetch<{ data: Product }>(`/products/${editingProd.id}`);
      if (refreshed?.data) setEditingProd(refreshed.data);
      load(false);
    } catch (err: any) {
      alert('Failed to set featured image: ' + (err?.detail || err?.title || err?.message || String(err)));
    }
  };

  const handleRemoveImage = async (mediaId: string) => {
    if (!editingProd) return;
    if (!confirm('Remove this image from garment?')) return;
    try {
      await apiFetch(`/products/${editingProd.id}/media/${mediaId}`, { method: 'DELETE' });
      const refreshed = await apiFetch<{ data: Product }>(`/products/${editingProd.id}`);
      if (refreshed?.data) setEditingProd(refreshed.data);
      load(false);
    } catch (err: any) {
      alert('Failed to remove image: ' + (err?.detail || err?.title || err?.message || String(err)));
    }
  };

  const handleAddVariant = async () => {
    if (!editingProd) return;
    const colorVal = newVariantColor.trim();
    const sizeVal = newVariantSize.trim();
    let title = newVariantTitle.trim();
    if (!title) {
      if (colorVal && sizeVal) title = `${colorVal} / ${sizeVal}`;
      else if (colorVal) title = `${colorVal} / Free Size`;
      else if (sizeVal) title = `Size ${sizeVal}`;
      else title = 'New Garment Variant';
    }

    setAddingVariant(true);
    try {
      const priceRupees = parseFloat(newVariantPrice) || 0;
      const stock = parseInt(newVariantStock, 10) || 0;
      const skuPrefix = editingProd.handle.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase() || 'PROD';
      const colOrTitlePart = (colorVal || title).replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase();
      const finalSku = newVariantSku.trim() || `ZAR-${skuPrefix}-${colOrTitlePart}-${Math.floor(1000 + Math.random() * 9000)}`;

      const optVals: Record<string, string> = {};
      if (colorVal) optVals['Color'] = colorVal;
      if (sizeVal) optVals['Size'] = sizeVal;

      await apiFetch(`/products/${editingProd.id}/variants`, {
        method: 'POST',
        body: JSON.stringify({
          title,
          sku: finalSku,
          priceMinor: Math.round(priceRupees * 100),
          onHand: stock,
          optionValues: optVals,
        }),
      });
      const refreshed = await apiFetch<{ data: Product }>(`/products/${editingProd.id}`);
      if (refreshed?.data) setEditingProd(refreshed.data);
      setNewVariantTitle('');
      setNewVariantColor('');
      setNewVariantSize('Free Size');
      setNewVariantSku('');
      setNewVariantPrice(String(priceRupees));
      setNewVariantStock('10');
      load(false);
    } catch (err: any) {
      alert('Failed to add variant: ' + (err?.detail || err?.title || err?.message || String(err)));
    } finally {
      setAddingVariant(false);
    }
  };

  const handleUpdateVariant = async (
    variantId: string,
    title: string,
    priceRupees: number,
    onHand: number,
    sku?: string,
    color?: string,
    size?: string
  ) => {
    if (!editingProd) return;
    setSavingVariantId(variantId);
    try {
      const currentV = editingProd.variants.find(v => v.id === variantId);
      const optVals = { ...(currentV?.optionValues || {}) };
      if (color !== undefined) {
        if (color.trim()) optVals['Color'] = color.trim();
        else delete optVals['Color'];
      }
      if (size !== undefined) {
        if (size.trim()) optVals['Size'] = size.trim();
        else delete optVals['Size'];
      }

      await apiFetch(`/products/${editingProd.id}/variants/${variantId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: title.trim(),
          sku: sku?.trim() || undefined,
          priceMinor: Math.round(priceRupees * 100),
          onHand,
          optionValues: optVals,
        }),
      });
      const refreshed = await apiFetch<{ data: Product }>(`/products/${editingProd.id}`);
      if (refreshed?.data) setEditingProd(refreshed.data);
      load(false);
    } catch (err: any) {
      alert('Failed to update variant: ' + (err?.detail || err?.title || err?.message || String(err)));
    } finally {
      setSavingVariantId(null);
    }
  };

  const handleDeleteVariant = async (variantId: string) => {
    if (!editingProd) return;
    if (editingProd.variants.length <= 1) {
      alert('Every garment must have at least one variant.');
      return;
    }
    if (!confirm('Are you sure you want to delete this variant?')) return;
    setDeletingVariantId(variantId);
    try {
      await apiFetch(`/products/${editingProd.id}/variants/${variantId}`, { method: 'DELETE' });
      const refreshed = await apiFetch<{ data: Product }>(`/products/${editingProd.id}`);
      if (refreshed?.data) setEditingProd(refreshed.data);
      load(false);
    } catch (err: any) {
      alert('Failed to delete variant: ' + (err?.detail || err?.title || err?.message || String(err)));
    } finally {
      setDeletingVariantId(null);
    }
  };

  const fmtPrice = (p: Product) => {
    const v = p.variants[0];
    if (!v) return '—';
    return `₹${(v.priceMinor / 100).toLocaleString('en-IN')}`;
  };

  const statusBadge = (s: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      active:   { bg: 'var(--admin-success-bg, rgba(16,185,129,0.1))',  color: '#10B981'  },
      draft:    { bg: 'var(--admin-warning-bg, rgba(232,163,61,0.1))',  color: '#E8A33D'  },
      archived: { bg: 'rgba(100,100,100,0.1)',                          color: '#8A8A8E' },
    };
    const st = map[s] ?? map.draft!;
    return (
      <span style={{ ...styles.badge, background: st.bg, color: st.color }}>
        {s}
      </span>
    );
  };

  const dirtyCount = bulkRows.filter(r => r.isDirty).length;

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Products & Catalog Merchandising</h1>
          <p style={styles.pageSubtitle}>{products.length} catalog garments • Phase 2 Spreadsheet Bulk Grid & Audit History</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* View toggle */}
          <div style={styles.modeToggle}>
            <button
              style={{ ...styles.modeBtn, ...(viewMode === 'list' ? styles.modeBtnActive : {}) }}
              onClick={() => setViewMode('list')}
            >
              <List size={14} /> Catalog List
            </button>
            <button
              style={{ ...styles.modeBtn, ...(viewMode === 'bulk_grid' ? styles.modeBtnActive : {}) }}
              onClick={() => setViewMode('bulk_grid')}
            >
              <Table size={14} /> Spreadsheet Bulk Grid
            </button>
          </div>

          {viewMode === 'bulk_grid' && (
            <button
              style={{
                ...styles.addBtn,
                background: dirtyCount > 0 ? '#10B981' : '#333',
                color: '#FFF',
                opacity: dirtyCount > 0 ? 1 : 0.6,
              }}
              onClick={handleSaveBulk}
              disabled={dirtyCount === 0 || savingBulk}
            >
              {savingBulk ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={15} />}
              Save All Changes {dirtyCount > 0 && `(${dirtyCount})`}
            </button>
          )}

          {viewMode === 'list' && (
            <button style={styles.addBtn} onClick={() => setShowAddModal(true)}>
              <Plus size={16} /> Add Garment
            </button>
          )}
        </div>
      </div>

      {bulkSuccess && (
        <div style={styles.successBanner}>
          <Check size={16} /> {bulkSuccess}
        </div>
      )}

      {/* Filters Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <Search size={15} style={styles.searchIcon} />
          <input
            style={styles.searchInput}
            placeholder="Search catalog garments, handles, fabrics…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          style={styles.select}
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* ── View 1: Standard Table List ── */}
      {viewMode === 'list' && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Garment</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Retail Price</th>
                <th style={styles.th}>Variants</th>
                <th style={styles.th}>Last Updated</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && products.length === 0 ? (
                <tr><td colSpan={6} style={styles.loadingCell}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /></td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan={6} style={styles.emptyCell}>No products found in catalog</td></tr>
              ) : products.map(p => (
                <tr key={p.id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={styles.productCell}>
                      <div style={styles.productThumb}>
                        {p.featuredImage?.url || p.images?.[0]?.url ? (
                          <img src={p.featuredImage?.url || p.images?.[0]?.url} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ background: '#2B2B2E', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ImageIcon size={16} color="#666" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p style={styles.productName}>{p.title}</p>
                        <a
                          href={getStorefrontProductUrl(p.handle)}
                          target="_blank"
                          rel="noreferrer"
                          style={{ ...styles.productHandle, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          title="Open piece on Storefront"
                        >
                          /{p.handle} <ExternalLink size={10} color="#8A8A8E" />
                        </a>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>{statusBadge(p.status)}</td>
                  <td style={styles.td}><span style={styles.price}>{fmtPrice(p)}</span></td>
                  <td style={styles.td}><span style={styles.variantCount}>{p.variants.length} pieces</span></td>
                  <td style={styles.td}><span style={styles.date}>{new Date(p.updatedAt).toLocaleDateString('en-IN')}</span></td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      <a
                        href={getStorefrontProductUrl(p.handle)}
                        target="_blank"
                        rel="noreferrer"
                        style={styles.iconBtn}
                        title="View live piece on Storefront"
                      >
                        <ExternalLink size={14} color="#C9A84C" />
                      </a>
                      <button
                        type="button"
                        style={styles.iconBtn}
                        title="Copy / Share Product Link"
                        onClick={() => handleShareProductLink(p)}
                      >
                        <Share2 size={14} color="#4ADE80" />
                      </button>
                      <button style={styles.iconBtn} title="Curate Complete the Look" onClick={() => openRecsModal(p)}>
                        <Sparkles size={14} color="#C9A84C" />
                      </button>
                      <button style={styles.iconBtn} title="Audit History & Restore" onClick={() => openHistory(p)}>
                        <History size={14} />
                      </button>
                      <button style={styles.iconBtn} title="Edit Product" onClick={() => handleOpenEdit(p)}>
                        <Edit2 size={14} />
                      </button>
                      <button style={styles.iconBtn} title={p.status === 'archived' ? 'Unarchive' : 'Archive'} onClick={() => handleToggleArchive(p)}>
                        <Archive size={14} color={p.status === 'archived' ? '#E8A33D' : undefined} />
                      </button>
                      <button style={{ ...styles.iconBtn, color: '#EF4444' }} title="Delete Product" onClick={() => handleDeleteProduct(p)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── View 2: High-Density Spreadsheet Bulk-Edit Grid ── */}
      {viewMode === 'bulk_grid' && (
        <div style={styles.tableWrap}>
          <div style={styles.gridNotice}>
            <strong>High-Density Spreadsheet Edit:</strong> Edit prices, stock counts, tags, and garment statuses inline below. Dirty rows are highlighted and committed atomically to Cloudflare D1 with audit history.
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Garment Variant</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Price (₹ INR)</th>
                <th style={styles.th}>On-Hand Stock</th>
                <th style={styles.th}>Tags</th>
                <th style={styles.th}>Status Indicator</th>
              </tr>
            </thead>
            <tbody>
              {bulkRows.map((row, idx) => (
                <tr key={`${row.productId}-${row.variantId}`} style={{ ...styles.tr, background: row.isDirty ? 'rgba(201, 168, 76, 0.08)' : 'transparent' }}>
                  <td style={styles.td}>
                    <div style={{ fontWeight: 500, color: '#FFF' }}>{row.title}</div>
                    <div style={{ fontSize: 11, color: '#8A8A8E' }}>/{row.handle}</div>
                  </td>
                  <td style={styles.td}>
                    <select
                      value={row.status}
                      onChange={e => handleBulkChange(idx, 'status', e.target.value)}
                      style={styles.gridSelect}
                    >
                      <option value="active">Active</option>
                      <option value="draft">Draft</option>
                      <option value="archived">Archived</option>
                    </select>
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number"
                      value={row.priceRupees}
                      onChange={e => handleBulkChange(idx, 'priceRupees', Number(e.target.value))}
                      style={styles.gridInput}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="number"
                      value={row.onHand}
                      onChange={e => handleBulkChange(idx, 'onHand', Number(e.target.value))}
                      style={{ ...styles.gridInput, width: 80 }}
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      type="text"
                      value={row.tags}
                      onChange={e => handleBulkChange(idx, 'tags', e.target.value)}
                      style={{ ...styles.gridInput, width: 220 }}
                      placeholder="e.g. linen, summer, classic"
                    />
                  </td>
                  <td style={styles.td}>
                    {row.isDirty ? (
                      <span style={{ fontSize: 11, color: '#C9A84C', fontWeight: 600 }}>Unsaved •</span>
                    ) : (
                      <span style={{ fontSize: 11, color: '#666' }}>Synced</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal: Add New Garment ── */}
      {showAddModal && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modalBox, width: '100%', maxWidth: 720 }}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Add New Garment to Catalog</h3>
                <p style={styles.modalSub}>Create a new product with real-time sync to Cloudflare D1</p>
              </div>
              <button style={styles.closeBtn} onClick={() => setShowAddModal(false)}><X size={18} /></button>
            </div>

            <form onSubmit={handleCreateProduct} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <div style={styles.modalBody}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>Garment Title *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. The Soba Merino Wool Cardigan"
                      value={newTitle}
                      onChange={e => setNewTitle(e.target.value)}
                      style={{ ...styles.searchInput, paddingLeft: 12 }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>URL Handle / Slug</label>
                      <input
                        type="text"
                        placeholder="e.g. soba-merino-cardigan"
                        value={newHandle}
                        onChange={e => setNewHandle(e.target.value)}
                        style={{ ...styles.searchInput, paddingLeft: 12 }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>Category (D1 Tree)</label>
                      <select
                        value={newCategoryId}
                        onChange={e => {
                          setNewCategoryId(e.target.value);
                          const cat = categories.find(c => c.id === e.target.value);
                          if (cat) setNewProductType(cat.title);
                        }}
                        style={{ ...styles.select, width: '100%' }}
                      >
                        <option value="">Unassigned</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.parentTitle ? `${c.parentTitle} > ${c.title}` : c.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Color / Finish Option */}
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>
                      Color / Finish (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Brown, Blue or Ivory, Onyx Black…"
                      value={newColorName}
                      onChange={e => setNewColorName(e.target.value)}
                      style={{ ...styles.searchInput, paddingLeft: 12 }}
                    />
                    {(() => {
                      const detected = newColorName.split(/[,/+|&]| and /i).map(c => c.trim()).filter(Boolean);
                      if (detected.length === 0) {
                        return (
                          <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                            Enter single or multiple colors (comma or slash separated, e.g. <em>Brown, Blue</em>). Distinct color variants & swatches will be generated.
                          </div>
                        );
                      }
                      return (
                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, color: '#C9A84C', fontWeight: 600 }}>
                            {detected.length > 1 ? `Detected ${detected.length} distinct colors:` : 'Color detected:'}
                          </span>
                          {detected.map((col, idx) => (
                            <span
                              key={idx}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '3px 8px',
                                borderRadius: 4,
                                background: '#25252A',
                                border: '1px solid #444',
                                fontSize: 11,
                                color: '#FAF7F2'
                              }}
                            >
                              <span
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  border: '1px solid rgba(255,255,255,0.4)',
                                  background: getAdminColorHex(col)
                                }}
                              />
                              {col}
                            </span>
                          ))}
                          {detected.length > 1 && (
                            <span style={{ fontSize: 10, color: '#8A8A8E' }}>
                              (Separate color variant will be generated for each)
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Product Imagery - Multi-image upload */}
                  <div style={{ background: '#202024', border: '1px solid #333', borderRadius: 8, padding: 14 }}>
                    <input
                      type="file"
                      ref={addFileInputRef}
                      accept="image/*"
                      multiple
                      onChange={e => handleUploadForAdd(e.target.files)}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#C9A84C', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <ImageIcon size={14} /> Product Images ({newImageUrls.length})
                      </label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => addFileInputRef.current?.click()}
                          disabled={uploadingImage && uploadingTarget === 'add'}
                          style={{
                            background: '#C9A84C',
                            color: '#000',
                            border: 'none',
                            borderRadius: 4,
                            padding: '5px 12px',
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5
                          }}
                        >
                          {uploadingImage && uploadingTarget === 'add' ? (
                            <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <UploadCloud size={12} />
                          )}
                          {uploadingImage && uploadingTarget === 'add' ? 'Uploading…' : 'Upload Images'}
                        </button>
                        <button
                          type="button"
                          onClick={() => openMediaPicker('add')}
                          style={{
                            background: 'transparent',
                            border: '1px solid #C9A84C',
                            color: '#C9A84C',
                            borderRadius: 4,
                            padding: '5px 12px',
                            fontSize: 11,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5
                          }}
                        >
                          <ImageIcon size={12} /> Media Library
                        </button>
                      </div>
                    </div>

                    {uploadingImage && uploadingTarget === 'add' && (
                      <div style={{ background: 'rgba(201, 168, 76, 0.12)', border: '1px solid #C9A84C', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#C9A84C', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        <span>{uploadProgressText || 'Uploading images to Cloudinary…'}</span>
                      </div>
                    )}

                    {/* Multiple Image Gallery Grid */}
                    {newImageUrls.length > 0 && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                        gap: 10,
                        marginBottom: 12
                      }}>
                        {newImageUrls.map((imgUrl, idx) => {
                          const isFeatured = idx === 0;
                          return (
                            <div
                              key={idx}
                              style={{
                                background: '#141416',
                                border: isFeatured ? '2px solid #C9A84C' : '1px solid #38383E',
                                borderRadius: 6,
                                overflow: 'hidden',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: isFeatured ? '0 0 10px rgba(201,168,76,0.25)' : 'none'
                              }}
                            >
                              <div style={{ height: 100, width: '100%', position: 'relative', background: '#0D0D0E' }}>
                                <img
                                  src={imgUrl}
                                  alt={`Garment image ${idx + 1}`}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                                />
                                {isFeatured && (
                                  <div style={{
                                    position: 'absolute',
                                    top: 4,
                                    left: 4,
                                    background: '#C9A84C',
                                    color: '#000',
                                    fontSize: 9,
                                    fontWeight: 700,
                                    padding: '2px 5px',
                                    borderRadius: 3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 3
                                  }}>
                                    <Star size={9} fill="#000" /> Featured
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={() => setNewImageUrls(prev => prev.filter((_, i) => i !== idx))}
                                  title="Remove image"
                                  style={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    background: 'rgba(0,0,0,0.7)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: 20,
                                    height: 20,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#FF6B6B',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <X size={12} />
                                </button>
                              </div>

                              {/* Quick action controls */}
                              <div style={{ padding: '4px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1A1A1E' }}>
                                {!isFeatured ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setNewImageUrls(prev => {
                                        const copy = [...prev];
                                        const [selected] = copy.splice(idx, 1);
                                        if (selected) copy.unshift(selected);
                                        return copy;
                                      });
                                    }}
                                    title="Make this the primary/featured image"
                                    style={{ background: 'transparent', border: 'none', color: '#C9A84C', fontSize: 10, cursor: 'pointer', padding: 0, fontWeight: 600 }}
                                  >
                                    Set Cover
                                  </button>
                                ) : (
                                  <span style={{ fontSize: 10, color: '#C9A84C', fontWeight: 600 }}>Cover</span>
                                )}
                                <div style={{ display: 'flex', gap: 4 }}>
                                  {idx > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setNewImageUrls(prev => {
                                          const copy = [...prev];
                                          const temp = copy[idx - 1]!;
                                          copy[idx - 1] = copy[idx]!;
                                          copy[idx] = temp;
                                          return copy;
                                        });
                                      }}
                                      title="Move left"
                                      style={{ background: '#25252A', border: 'none', color: '#AAA', borderRadius: 2, padding: '1px 5px', fontSize: 9, cursor: 'pointer' }}
                                    >
                                      ◀
                                    </button>
                                  )}
                                  {idx < newImageUrls.length - 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setNewImageUrls(prev => {
                                          const copy = [...prev];
                                          const temp = copy[idx + 1]!;
                                          copy[idx + 1] = copy[idx]!;
                                          copy[idx] = temp;
                                          return copy;
                                        });
                                      }}
                                      title="Move right"
                                      style={{ background: '#25252A', border: 'none', color: '#AAA', borderRadius: 2, padding: '1px 5px', fontSize: 9, cursor: 'pointer' }}
                                    >
                                      ▶
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Drag & drop dropzone supporting multiple files */}
                    <div
                      onClick={() => addFileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUploadForAdd(e.dataTransfer.files);
                      }}
                      style={{
                        border: '1px dashed #555',
                        background: '#18181B',
                        borderRadius: 6,
                        padding: '12px 16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        marginBottom: 10,
                        transition: 'border-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 10
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#555')}
                    >
                      <UploadCloud size={20} color="#C9A84C" />
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#E4E4E7' }}>
                          Drag & drop garment photos here, or <span style={{ color: '#C9A84C', textDecoration: 'underline' }}>browse device</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#8A8A8E' }}>
                          Select multiple images at once (JPG, PNG, WEBP). First image is set as Featured.
                        </div>
                      </div>
                    </div>

                    {/* Direct URL input row */}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="url"
                        placeholder="Or paste image URL(s) (comma-separated)..."
                        value={newImageUrlInput}
                        onChange={e => setNewImageUrlInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (newImageUrlInput.trim()) {
                              const urls = newImageUrlInput.split(/[\n,]+/).map(u => u.trim()).filter(Boolean);
                              setNewImageUrls(prev => [...prev, ...urls]);
                              setNewImageUrlInput('');
                            }
                          }
                        }}
                        style={{ ...styles.searchInput, paddingLeft: 12, flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (newImageUrlInput.trim()) {
                            const urls = newImageUrlInput.split(/[\n,]+/).map(u => u.trim()).filter(Boolean);
                            setNewImageUrls(prev => [...prev, ...urls]);
                            setNewImageUrlInput('');
                          }
                        }}
                        disabled={!newImageUrlInput.trim()}
                        style={{
                          ...styles.saveBtn,
                          padding: '0 14px',
                          fontSize: 12,
                          opacity: newImageUrlInput.trim() ? 1 : 0.5,
                          cursor: newImageUrlInput.trim() ? 'pointer' : 'default',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        + Add URL
                      </button>
                    </div>
                  </div>

                  {/* Sizing & Variants Setup */}
                  <div style={{ background: '#202024', border: '1px solid #333', borderRadius: 8, padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: '#C9A84C', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Layers size={14} /> Sizing & Variant Setup
                      </label>
                      <div style={{ display: 'flex', background: '#141416', borderRadius: 6, padding: 2, border: '1px solid #333' }}>
                        <button
                          type="button"
                          onClick={() => setVariantMode('single')}
                          style={{
                            padding: '4px 10px', fontSize: 11, border: 'none', borderRadius: 4, cursor: 'pointer',
                            background: variantMode === 'single' ? '#2E2E34' : 'transparent',
                            color: variantMode === 'single' ? '#FFF' : '#8A8A8E',
                            fontWeight: variantMode === 'single' ? 600 : 400
                          }}
                        >
                          Single Size / Free Size
                        </button>
                        <button
                          type="button"
                          onClick={() => setVariantMode('sizes')}
                          style={{
                            padding: '4px 10px', fontSize: 11, border: 'none', borderRadius: 4, cursor: 'pointer',
                            background: variantMode === 'sizes' ? '#C9A84C' : 'transparent',
                            color: variantMode === 'sizes' ? '#000' : '#8A8A8E',
                            fontWeight: variantMode === 'sizes' ? 600 : 400
                          }}
                        >
                          Multi-Size (XS - XL)
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: variantMode === 'sizes' ? 12 : 0 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#AAA', marginBottom: 4 }}>
                          {variantMode === 'sizes' ? 'Base Retail Price (₹ INR) *' : 'Retail Price (₹ INR) *'}
                        </label>
                        <input
                          type="number"
                          required
                          value={newPriceRupees}
                          onChange={e => setNewPriceRupees(e.target.value)}
                          style={{ ...styles.searchInput, paddingLeft: 12 }}
                        />
                      </div>
                      {variantMode === 'single' ? (
                        <div>
                          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#AAA', marginBottom: 4 }}>Initial Stock (Units) *</label>
                          <input
                            type="number"
                            required
                            value={newStock}
                            onChange={e => setNewStock(e.target.value)}
                            style={{ ...styles.searchInput, paddingLeft: 12 }}
                          />
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', fontSize: 11, color: '#8A8A8E', paddingTop: 18 }}>
                          Individual stock levels for each size are configured below.
                        </div>
                      )}
                    </div>

                    {variantMode === 'sizes' && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #333' }}>
                        <div style={{ fontSize: 11, color: '#8A8A8E', marginBottom: 8 }}>Allocate initial stock per garment size:</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(68px, 1fr))', gap: 8 }}>
                          {multiSizes.map((s, idx) => (
                            <div key={s.size} style={{ background: '#141416', border: '1px solid #333', borderRadius: 6, padding: '8px 10px', textAlign: 'center' }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#C9A84C', marginBottom: 4 }}>Size {s.size}</div>
                              <input
                                type="number"
                                min="0"
                                value={s.stock}
                                onChange={e => {
                                  const val = parseInt(e.target.value, 10) || 0;
                                  setMultiSizes(prev => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx]!, stock: val };
                                    return next;
                                  });
                                }}
                                style={{ ...styles.gridInput, width: '100%', textAlign: 'center' }}
                              />
                              <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>Units</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>Tags (comma-separated)</label>
                    <input
                      type="text"
                      placeholder="e.g. Linen, Summer, AW26, Bestseller"
                      value={newTags}
                      onChange={e => setNewTags(e.target.value)}
                      style={{ ...styles.searchInput, paddingLeft: 12 }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>Description</label>
                    <textarea
                      rows={3}
                      placeholder="Handcrafted tailoring, fabric details, and silhouette description..."
                      value={newDesc}
                      onChange={e => setNewDesc(e.target.value)}
                      style={{ width: '100%', background: '#1E1E22', border: '1px solid #333', borderRadius: 6, padding: '8px 12px', color: '#FFF', fontSize: 13, resize: 'vertical', outline: 'none' }}
                    />
                  </div>
                </div>
              </div>

              {/* Pinned Sticky Modal Footer */}
              <div style={styles.modalFooter}>
                <button type="button" style={styles.cancelBtn} onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" style={styles.saveBtn} disabled={creatingProduct}>
                  {creatingProduct ? 'Creating Garment…' : 'Publish Garment to Catalog'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Edit Garment ── */}
      {editingProd && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modalBox, width: '100%', maxWidth: 780, maxHeight: '92vh' }}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Edit Garment: {editingProd.title}</h3>
                <p style={styles.modalSub}>Manage attributes, imagery, and sizing variants</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <a
                  href={getStorefrontProductUrl(editingProd.handle)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ ...styles.iconBtn, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', fontSize: 12, color: '#C9A84C', border: '1px solid #333', textDecoration: 'none' }}
                  title="Open live product on Storefront"
                >
                  <ExternalLink size={13} /> View Live
                </a>
                <button
                  type="button"
                  onClick={() => handleShareProductLink(editingProd)}
                  style={{ ...styles.iconBtn, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', fontSize: 12, color: '#4ADE80', border: '1px solid #333' }}
                  title="Copy Shareable Product Link"
                >
                  <Share2 size={13} /> Copy Link
                </button>
                <button style={styles.closeBtn} onClick={() => setEditingProd(null)}><X size={18} /></button>
              </div>
            </div>

            {/* Modal Tabs */}
            <div style={styles.tabsHeader}>
              <button
                type="button"
                onClick={() => setEditTab('details')}
                style={{ ...styles.tabBtn, ...(editTab === 'details' ? styles.tabBtnActive : {}) }}
              >
                Garment Details
              </button>
              <button
                type="button"
                onClick={() => setEditTab('media')}
                style={{ ...styles.tabBtn, ...(editTab === 'media' ? styles.tabBtnActive : {}) }}
              >
                Imagery & Media ({editingProd.images?.length || 0})
              </button>
              <button
                type="button"
                onClick={() => setEditTab('variants')}
                style={{ ...styles.tabBtn, ...(editTab === 'variants' ? styles.tabBtnActive : {}) }}
              >
                Variants & Sizing ({editingProd.variants?.length || 0})
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* Tab 1: Details */}
              {editTab === 'details' && (
                <form onSubmit={handleSaveEdit}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>Garment Title *</label>
                      <input
                        type="text"
                        required
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        style={{ ...styles.searchInput, paddingLeft: 12 }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>URL Handle / Slug</label>
                        <input
                          type="text"
                          value={editHandle}
                          onChange={e => setEditHandle(e.target.value)}
                          style={{ ...styles.searchInput, paddingLeft: 12 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>Category (D1 Tree)</label>
                        <select
                          value={editCategoryId}
                          onChange={e => {
                            setEditCategoryId(e.target.value);
                            const cat = categories.find(c => c.id === e.target.value);
                            if (cat) setEditProductType(cat.title);
                          }}
                          style={{ ...styles.select, width: '100%' }}
                        >
                          <option value="">Unassigned</option>
                          {categories.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.parentTitle ? `${c.parentTitle} > ${c.title}` : c.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>Status</label>
                      <select
                        value={editStatus}
                        onChange={e => setEditStatus(e.target.value as any)}
                        style={{ ...styles.select, width: '100%' }}
                      >
                        <option value="active">Active (Visible in Storefront)</option>
                        <option value="draft">Draft (Hidden)</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>Tags (comma-separated)</label>
                      <input
                        type="text"
                        value={editTags}
                        onChange={e => setEditTags(e.target.value)}
                        style={{ ...styles.searchInput, paddingLeft: 12 }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 6 }}>Description</label>
                      <textarea
                        rows={4}
                        value={editDesc}
                        onChange={e => setEditDesc(e.target.value)}
                        style={{ width: '100%', background: '#1E1E22', border: '1px solid #333', borderRadius: 6, padding: '8px 12px', color: '#FFF', fontSize: 13, resize: 'vertical', outline: 'none' }}
                      />
                    </div>

                    {/* Primary Photo Quick Action */}
                    <div style={{ background: '#202024', border: '1px solid #333', borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 48, height: 48, borderRadius: 6, background: '#141416', overflow: 'hidden', border: '1px solid #444', flexShrink: 0 }}>
                          {editingProd.featuredImage?.url ? (
                            <img src={editingProd.featuredImage.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : editingProd.images && editingProd.images.length > 0 ? (
                            <img src={editingProd.images[0]?.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', color: '#666' }}>
                              <ImageIcon size={18} />
                            </div>
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#FFF' }}>Garment Imagery</div>
                          <div style={{ fontSize: 11, color: '#8A8A8E' }}>
                            {editingProd.images?.length || 0} image{(editingProd.images?.length || 0) === 1 ? '' : 's'} attached
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => {
                            setEditTab('media');
                            setTimeout(() => editFileInputRef.current?.click(), 100);
                          }}
                          style={{
                            background: '#2A2A30',
                            border: '1px solid #444',
                            color: '#C9A84C',
                            borderRadius: 4,
                            padding: '6px 12px',
                            fontSize: 11,
                            fontWeight: 500,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5
                          }}
                        >
                          <UploadCloud size={12} /> Upload New Photo
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditTab('media')}
                          style={{
                            background: 'transparent',
                            border: '1px solid #333',
                            color: '#AAA',
                            borderRadius: 4,
                            padding: '6px 10px',
                            fontSize: 11,
                            cursor: 'pointer'
                          }}
                        >
                          Manage Gallery
                        </button>
                      </div>
                    </div>

                    <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                      <button type="button" style={styles.cancelBtn} onClick={() => setEditingProd(null)}>Cancel</button>
                      <button type="submit" style={styles.saveBtn} disabled={savingEdit}>
                        {savingEdit ? 'Saving…' : 'Save Details'}
                      </button>
                    </div>
                  </div>
                </form>
              )}

              {/* Tab 2: Imagery & Media */}
              {editTab === 'media' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {/* Hidden file input for native file picker */}
                  <input
                    type="file"
                    ref={editFileInputRef}
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => handleUploadForEdit(e.target.files)}
                  />

                  {/* Attach & Upload Section */}
                  <div style={{ background: '#202024', border: '1px solid #333', borderRadius: 8, padding: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#FFF', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <UploadCloud size={15} color="#C9A84C" /> Add Garment Imagery
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => editFileInputRef.current?.click()}
                          disabled={uploadingImage && uploadingTarget === 'edit'}
                          style={{
                            background: '#C9A84C',
                            color: '#000',
                            border: 'none',
                            borderRadius: 4,
                            padding: '6px 12px',
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 5
                          }}
                        >
                          {uploadingImage && uploadingTarget === 'edit' ? (
                            <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <UploadCloud size={12} />
                          )}
                          {uploadingImage && uploadingTarget === 'edit' ? 'Uploading…' : 'Upload From Device'}
                        </button>
                        <button
                          type="button"
                          onClick={() => openMediaPicker('edit')}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#2A2A30', border: '1px solid #444', color: '#C9A84C', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 500 }}
                        >
                          <ImageIcon size={12} /> Media Library
                        </button>
                      </div>
                    </div>

                    {uploadingImage && uploadingTarget === 'edit' && (
                      <div style={{ background: 'rgba(201, 168, 76, 0.12)', border: '1px solid #C9A84C', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#C9A84C', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        <span>{uploadProgressText || 'Uploading image to Cloudinary and attaching to garment…'}</span>
                      </div>
                    )}

                    {/* Drag and Drop dropzone */}
                    <div
                      onClick={() => editFileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUploadForEdit(e.dataTransfer.files);
                      }}
                      style={{
                        border: '1px dashed #555',
                        background: '#18181B',
                        borderRadius: 6,
                        padding: '14px 18px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        marginBottom: 12,
                        transition: 'border-color 0.2s',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#C9A84C')}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#555')}
                    >
                      <UploadCloud size={22} color="#C9A84C" />
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#E4E4E7' }}>
                        Drag & drop garment photos here, or <span style={{ color: '#C9A84C', textDecoration: 'underline' }}>browse device</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#8A8A8E' }}>
                        JPG, PNG, WEBP auto-uploaded to Cloudinary and attached to this garment
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <input
                        type="url"
                        placeholder="Or attach via direct URL (https://res.cloudinary.com/...)..."
                        value={editImageUrl}
                        onChange={e => setEditImageUrl(e.target.value)}
                        style={{ ...styles.searchInput, paddingLeft: 12, flex: 1, minWidth: 260 }}
                      />
                      <button
                        type="button"
                        onClick={() => handleAttachImage(editImageUrl)}
                        disabled={addingImage || !editImageUrl.trim()}
                        style={styles.saveBtn}
                      >
                        {addingImage ? 'Attaching…' : 'Attach URL'}
                      </button>
                    </div>
                  </div>

                  {/* Attached Images Grid */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Attached Garment Imagery ({editingProd.images?.length || 0})
                    </div>
                    {(!editingProd.images || editingProd.images.length === 0) ? (
                      <div style={{ textAlign: 'center', padding: '30px 20px', background: '#1A1A1E', border: '1px dashed #333', borderRadius: 8, color: '#8A8A8E', fontSize: 13 }}>
                        <ImageIcon size={32} color="#555" style={{ marginBottom: 8 }} />
                        <div>No images attached to this garment yet.</div>
                        <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>Add an image URL above or pick from your uploaded Media Library.</div>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                        {editingProd.images.map(img => {
                          const isFeatured = editingProd.featuredImage?.id === img.id;
                          return (
                            <div key={img.id} style={{ background: '#202024', border: isFeatured ? '1px solid #C9A84C' : '1px solid #333', borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                              <div style={{ height: 140, background: '#141416', position: 'relative' }}>
                                <img src={img.url} alt={img.altText || editingProd.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                {isFeatured && (
                                  <div style={{ position: 'absolute', top: 6, left: 6, background: '#C9A84C', color: '#000', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Star size={10} fill="#000" /> Featured
                                  </div>
                                )}
                              </div>
                              <div style={{ padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1A1A1E' }}>
                                {!isFeatured ? (
                                  <button
                                    type="button"
                                    onClick={() => handleSetFeaturedImage(img.id)}
                                    title="Set as featured thumbnail"
                                    style={{ fontSize: 11, color: '#C9A84C', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 500 }}
                                  >
                                    ★ Make Featured
                                  </button>
                                ) : (
                                  <span style={{ fontSize: 11, color: '#C9A84C', fontWeight: 600 }}>Default</span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveImage(img.id)}
                                  title="Remove image"
                                  style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer', padding: 2 }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tab 3: Variants & Sizing */}
              {editTab === 'variants' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {/* Current Variants Table */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#AAA', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Active Garment Variants ({editingProd.variants?.length || 0})
                    </div>
                    <div style={{ background: '#1A1A1E', border: '1px solid #333', borderRadius: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                        <thead>
                          <tr style={{ background: '#141416', borderBottom: '1px solid #333' }}>
                            <th style={{ ...styles.th, padding: '8px 12px' }}>Variant Title</th>
                            <th style={{ ...styles.th, padding: '8px 12px' }}>SKU</th>
                            <th style={{ ...styles.th, padding: '8px 12px' }}>Retail Price (₹)</th>
                            <th style={{ ...styles.th, padding: '8px 12px' }}>Stock</th>
                            <th style={{ ...styles.th, padding: '8px 12px', textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editingProd.variants.map(v => (
                            <VariantRowItem
                              key={v.id}
                              variant={v}
                              isSaving={savingVariantId === v.id}
                              isDeleting={deletingVariantId === v.id}
                              canDelete={editingProd.variants.length > 1}
                              onSave={(title, price, stock, sku, color, size) => handleUpdateVariant(v.id, title, price, stock, sku, color, size)}
                              onDelete={() => handleDeleteVariant(v.id)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Add New Variant Section */}
                  <div style={{ background: '#202024', border: '1px solid #333', borderRadius: 8, padding: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#C9A84C', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Plus size={15} /> Add New Garment Variant (Color & Sizing)
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#AAA', marginBottom: 4 }}>Color Swatch</label>
                        <input
                          type="text"
                          placeholder="e.g. Blue, Brown"
                          value={newVariantColor}
                          onChange={e => setNewVariantColor(e.target.value)}
                          style={{ ...styles.searchInput, paddingLeft: 10 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#AAA', marginBottom: 4 }}>Size</label>
                        <input
                          type="text"
                          placeholder="e.g. Free Size, M, L"
                          value={newVariantSize}
                          onChange={e => setNewVariantSize(e.target.value)}
                          style={{ ...styles.searchInput, paddingLeft: 10 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#AAA', marginBottom: 4 }}>Title (Optional)</label>
                        <input
                          type="text"
                          placeholder="Auto-generated if empty"
                          value={newVariantTitle}
                          onChange={e => setNewVariantTitle(e.target.value)}
                          style={{ ...styles.searchInput, paddingLeft: 10 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#AAA', marginBottom: 4 }}>SKU</label>
                        <input
                          type="text"
                          placeholder="Auto-generated if empty"
                          value={newVariantSku}
                          onChange={e => setNewVariantSku(e.target.value)}
                          style={{ ...styles.searchInput, paddingLeft: 10 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#AAA', marginBottom: 4 }}>Price (₹ INR)</label>
                        <input
                          type="number"
                          value={newVariantPrice}
                          onChange={e => setNewVariantPrice(e.target.value)}
                          style={{ ...styles.searchInput, paddingLeft: 10 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 11, color: '#AAA', marginBottom: 4 }}>Units</label>
                        <input
                          type="number"
                          value={newVariantStock}
                          onChange={e => setNewVariantStock(e.target.value)}
                          style={{ ...styles.searchInput, paddingLeft: 10 }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleAddVariant}
                        disabled={addingVariant}
                        style={{ ...styles.saveBtn, height: 38, display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        {addingVariant ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={14} />}
                        Add Variant
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pinned Sticky Modal Footer for Edit Garment */}
            <div style={styles.modalFooter}>
              <button type="button" style={styles.cancelBtn} onClick={() => setEditingProd(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Media Library Picker ── */}
      {showMediaPicker && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modalBox, width: '100%', maxWidth: 720, maxHeight: '88vh' }}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Choose from Media Library</h3>
                <p style={styles.modalSub}>Select high-resolution garment imagery uploaded via Cloudinary</p>
              </div>
              <button style={styles.closeBtn} onClick={() => setShowMediaPicker(false)}><X size={18} /></button>
            </div>

            <div style={{ ...styles.modalBody, padding: 16 }}>
              {/* Native file input for direct picker uploads */}
              <input
                type="file"
                ref={pickerFileInputRef}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleUploadForPicker(e.target.files)}
              />

              {/* Action bar with Upload trigger */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, background: '#202024', padding: '10px 14px', borderRadius: 6, border: '1px solid #333', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 12, color: '#AAA' }}>
                  Select an existing asset, or upload a new photo directly from your device:
                </div>
                <button
                  type="button"
                  onClick={() => pickerFileInputRef.current?.click()}
                  disabled={uploadingImage && uploadingTarget === 'picker'}
                  style={{
                    background: '#C9A84C',
                    color: '#000',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  {uploadingImage && uploadingTarget === 'picker' ? (
                    <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <UploadCloud size={13} />
                  )}
                  {uploadingImage && uploadingTarget === 'picker' ? 'Uploading…' : 'Upload New Photo'}
                </button>
              </div>

              {uploadingImage && uploadingTarget === 'picker' && (
                <div style={{ background: 'rgba(201, 168, 76, 0.12)', border: '1px solid #C9A84C', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#C9A84C', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>{uploadProgressText || 'Uploading image to Cloudinary…'}</span>
                </div>
              )}

              {loadingMedia ? (
                <div style={{ textAlign: 'center', padding: 40 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
              ) : mediaAssets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8A8A8E' }}>
                  <ImageIcon size={36} color="#555" style={{ marginBottom: 12 }} />
                  <div>No media assets found in your Cloudinary repository.</div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                    You can upload photos and campaign videos in the <strong>Media</strong> tab, or enter direct URLs.
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 }}>
                  {mediaAssets.map(asset => (
                    <div
                      key={asset.id}
                      onClick={() => handleSelectMediaAsset(asset)}
                      style={{
                        background: '#202024', border: '1px solid #333', borderRadius: 6, overflow: 'hidden',
                        cursor: 'pointer', transition: 'border-color 0.15s, transform 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#C9A84C'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      <div style={{ height: 110, background: '#141416' }}>
                        <img src={asset.url} alt={asset.altText || 'Media Asset'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '6px 8px', fontSize: 11, color: '#AAA', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {asset.altText || asset.key?.split('/').pop() || 'Asset'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowMediaPicker(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      {historyModalProd && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Revision History: {historyModalProd.title}</h3>
                <p style={styles.modalSub}>Immutable audit logs with 1-click snapshot restore</p>
              </div>
              <button style={styles.closeBtn} onClick={() => setHistoryModalProd(null)}><X size={18} /></button>
            </div>

            <div style={styles.modalBody}>
              {loadingHistory ? (
                <div style={{ textAlign: 'center', padding: 40 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
              ) : auditLogs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#8A8A8E' }}>No revisions recorded for this garment yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {auditLogs.map(log => (
                    <div key={log.id} style={styles.logCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={styles.logBadge}>{log.action.replace('_', ' ').toUpperCase()}</span>
                          <span style={{ fontSize: 12, color: '#8A8A8E', marginLeft: 8 }}>
                            {new Date(log.createdAt).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <button
                          style={styles.restoreBtn}
                          onClick={() => restoreSnapshot(log.id)}
                          disabled={restoringId === log.id}
                        >
                          {restoringId === log.id ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RotateCcw size={13} />}
                          Restore Snapshot
                        </button>
                      </div>
                      <pre style={styles.logDiff}>{JSON.stringify(log.diff, null, 2)}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setHistoryModalProd(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Curate Complete the Look Recommendations ── */}
      {recModalProd && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Curate Ensemble: {recModalProd.title}</h3>
                <p style={styles.modalSub}>Select companion pieces to display in "Complete the Look" on Storefront PDP</p>
              </div>
              <button style={styles.closeBtn} onClick={() => setRecModalProd(null)}><X size={18} /></button>
            </div>

            <div style={styles.modalBody}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {products.filter(p => p.id !== recModalProd.id).map(p => {
                  const isSelected = selectedRecIds.includes(p.id);
                  return (
                    <div
                      key={p.id}
                      onClick={() => {
                        setSelectedRecIds(prev =>
                          prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id]
                        );
                      }}
                      style={{
                        ...styles.recPickItem,
                        border: isSelected ? '1px solid #C9A84C' : '1px solid #333',
                        background: isSelected ? 'rgba(201, 168, 76, 0.1)' : '#1E1E22',
                      }}
                    >
                      <input type="checkbox" checked={isSelected} readOnly style={{ marginRight: 12 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, color: '#FFF', fontSize: 13 }}>{p.title}</div>
                        <div style={{ fontSize: 11, color: '#8A8A8E' }}>/{p.handle}</div>
                      </div>
                      <div style={{ color: '#C9A84C', fontWeight: 600, fontSize: 13 }}>{fmtPrice(p)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setRecModalProd(null)}>Cancel</button>
              <button style={styles.saveBtn} onClick={saveRecommendations} disabled={savingRec}>
                {savingRec ? 'Saving…' : 'Save Curated Ensemble'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function VariantRowItem({
  variant,
  isSaving,
  isDeleting,
  canDelete,
  onSave,
  onDelete,
}: {
  variant: Variant;
  isSaving: boolean;
  isDeleting: boolean;
  canDelete: boolean;
  onSave: (title: string, priceRupees: number, onHand: number, sku?: string, color?: string, size?: string) => void;
  onDelete: () => void;
}) {
  const [title, setTitle]             = useState(variant.title);
  const [sku, setSku]                 = useState(variant.sku || '');
  const [color, setColor]             = useState(variant.optionValues?.Color || variant.optionValues?.color || '');
  const [size, setSize]               = useState(variant.optionValues?.Size || variant.optionValues?.size || '');
  const [priceRupees, setPriceRupees] = useState(String(Math.round(variant.priceMinor / 100)));
  const [onHand, setOnHand]           = useState(String(variant.onHand ?? 0));
  const [isDirty, setIsDirty]         = useState(false);

  return (
    <tr style={{ borderBottom: '1px solid #28282C' }}>
      <td style={{ padding: '8px 12px' }}>
        <input
          type="text"
          value={title}
          onChange={e => { setTitle(e.target.value); setIsDirty(true); }}
          style={{ ...styles.gridInput, width: 140 }}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: getAdminColorHex(color),
                border: '1px solid rgba(255,255,255,0.4)',
                flexShrink: 0
              }}
            />
            <input
              type="text"
              placeholder="Color"
              value={color}
              onChange={e => { setColor(e.target.value); setIsDirty(true); }}
              title="Variant Color option"
              style={{ ...styles.gridInput, width: 70, fontSize: 10, padding: '2px 4px' }}
            />
          </div>
          <input
            type="text"
            placeholder="Size"
            value={size}
            onChange={e => { setSize(e.target.value); setIsDirty(true); }}
            title="Variant Size option"
            style={{ ...styles.gridInput, width: 55, fontSize: 10, padding: '2px 4px' }}
          />
        </div>
      </td>
      <td style={{ padding: '8px 12px' }}>
        <input
          type="text"
          value={sku}
          onChange={e => { setSku(e.target.value); setIsDirty(true); }}
          style={{ ...styles.gridInput, width: 130, fontSize: 11, color: '#AAA' }}
        />
      </td>
      <td style={{ padding: '8px 12px' }}>
        <input
          type="number"
          value={priceRupees}
          onChange={e => { setPriceRupees(e.target.value); setIsDirty(true); }}
          style={{ ...styles.gridInput, width: 85 }}
        />
      </td>
      <td style={{ padding: '8px 12px' }}>
        <input
          type="number"
          value={onHand}
          onChange={e => { setOnHand(e.target.value); setIsDirty(true); }}
          style={{ ...styles.gridInput, width: 65 }}
        />
      </td>
      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <button
            type="button"
            disabled={isSaving || !isDirty}
            onClick={() => {
              onSave(title, parseFloat(priceRupees) || 0, parseInt(onHand, 10) || 0, sku, color, size);
              setIsDirty(false);
            }}
            style={{
              padding: '4px 10px', fontSize: 11, borderRadius: 4, border: 'none', cursor: isDirty ? 'pointer' : 'default',
              background: isDirty ? '#C9A84C' : '#2A2A30', color: isDirty ? '#000' : '#666', fontWeight: 600,
            }}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            disabled={isDeleting || !canDelete}
            onClick={onDelete}
            title={canDelete ? 'Delete variant' : 'Cannot delete the only variant'}
            style={{
              background: 'transparent', border: '1px solid #38383E', color: canDelete ? '#EF4444' : '#555',
              borderRadius: 4, width: 28, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              cursor: canDelete ? 'pointer' : 'not-allowed',
            }}
          >
            {isDeleting ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={12} />}
          </button>
        </div>
      </td>
    </tr>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page:          { maxWidth: 1200, margin: '0 auto', color: '#E5E5E5', width: '100%' },
  pageHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  pageTitle:     { fontSize: 24, fontWeight: 600, color: '#FFF', marginBottom: 4, fontFamily: "'Playfair Display', serif" },
  pageSubtitle:  { fontSize: 13, color: '#8A8A8E' },
  addBtn:        { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#C9A84C', color: '#1A1A1A', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  modeToggle:    { display: 'flex', background: '#1E1E22', border: '1px solid #333', borderRadius: 6, padding: 3, flexWrap: 'wrap' },
  modeBtn:       { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'transparent', border: 'none', color: '#8A8A8E', fontSize: 12, borderRadius: 4, cursor: 'pointer' },
  modeBtnActive: { background: '#2E2E34', color: '#FFF', fontWeight: 600 },
  successBanner: { padding: '10px 16px', background: 'rgba(16,185,129,0.15)', border: '1px solid #10B981', color: '#10B981', borderRadius: 6, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 },
  gridNotice:    { padding: '10px 16px', background: '#1E1E22', borderBottom: '1px solid #333', color: '#BBB', fontSize: 12 },
  toolbar:       { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' },
  searchWrap:    { position: 'relative', flex: 1, minWidth: 200 },
  searchIcon:    { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#8A8A8E' },
  searchInput:   { width: '100%', padding: '9px 12px 9px 34px', background: '#1E1E22', border: '1px solid #333', borderRadius: 6, fontSize: 13, color: '#FFF', outline: 'none' },
  select:        { padding: '9px 12px', background: '#1E1E22', border: '1px solid #333', borderRadius: 6, fontSize: 13, color: '#FFF', outline: 'none', cursor: 'pointer' },
  tableWrap:     { background: '#1E1E22', border: '1px solid #333', borderRadius: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' },
  table:         { width: '100%', borderCollapse: 'collapse', minWidth: 620 },
  th:            { padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#8A8A8E', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid #333', background: '#18181B' },
  tr:            { borderBottom: '1px solid #28282C', transition: 'background 0.15s' },
  td:            { padding: '12px 16px', verticalAlign: 'middle', fontSize: 13 },
  loadingCell:   { padding: 40, textAlign: 'center', color: '#8A8A8E' },
  emptyCell:     { padding: 40, textAlign: 'center', color: '#8A8A8E', fontSize: 13 },
  productCell:   { display: 'flex', alignItems: 'center', gap: 12 },
  productThumb:  { width: 40, height: 40, borderRadius: 4, overflow: 'hidden', background: '#2E2E34', flexShrink: 0 },
  productName:   { fontWeight: 500, color: '#FFF', marginBottom: 2 },
  productHandle: { fontSize: 11, color: '#8A8A8E' },
  badge:         { display: 'inline-flex', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, textTransform: 'capitalize', letterSpacing: '0.03em' },
  price:         { fontWeight: 600, color: '#FFF' },
  variantCount:  { fontWeight: 500, color: '#8A8A8E' },
  date:          { color: '#8A8A8E', fontSize: 12 },
  actions:       { display: 'flex', gap: 6 },
  iconBtn:       { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, background: '#28282C', border: '1px solid #38383E', borderRadius: 4, color: '#AAA', cursor: 'pointer' },
  gridInput:     { background: '#141416', border: '1px solid #333', borderRadius: 4, color: '#FFF', padding: '6px 8px', fontSize: 13, outline: 'none' },
  gridSelect:    { background: '#141416', border: '1px solid #333', borderRadius: 4, color: '#FFF', padding: '6px 8px', fontSize: 13, outline: 'none', cursor: 'pointer' },
  modalOverlay:  { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 },
  modalBox:      { background: '#18181B', border: '1px solid #333', borderRadius: 8, width: '100%', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.7)' },
  modalHeader:   { padding: '14px 20px', borderBottom: '1px solid #2E2E34', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, background: '#18181B' },
  modalTitle:    { margin: 0, fontSize: 16, fontWeight: 600, color: '#FFF' },
  modalSub:      { margin: '4px 0 0', fontSize: 12, color: '#8A8A8E' },
  modalBody:     { padding: '18px 20px', overflowY: 'auto', flex: 1, minHeight: 0, WebkitOverflowScrolling: 'touch' },
  modalFooter:   { padding: '12px 20px', borderTop: '1px solid #2E2E34', background: '#141416', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' },
  closeBtn:      { background: 'transparent', border: 'none', color: '#8A8A8E', cursor: 'pointer' },
  tabsHeader:    { display: 'flex', borderBottom: '1px solid #2E2E34', background: '#141416', padding: '0 16px', overflowX: 'auto' },
  tabBtn:        { padding: '10px 16px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#8A8A8E', fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  tabBtnActive:  { color: '#C9A84C', borderBottom: '2px solid #C9A84C', fontWeight: 600 },
  logCard:       { background: '#202024', border: '1px solid #333', borderRadius: 6, padding: 12 },
  logBadge:      { fontSize: 10, fontWeight: 700, padding: '2px 6px', background: '#C9A84C', color: '#000', borderRadius: 4 },
  restoreBtn:    { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: '#333', border: '1px solid #444', color: '#FFF', borderRadius: 4, fontSize: 11, cursor: 'pointer' },
  logDiff:       { background: '#141416', padding: 8, borderRadius: 4, fontSize: 11, color: '#AAA', margin: '8px 0 0', overflowX: 'auto' },
  recPickItem:   { display: 'flex', alignItems: 'center', padding: '10px 14px', borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s' },
  cancelBtn:     { padding: '8px 16px', background: 'transparent', border: '1px solid #444', color: '#AAA', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  saveBtn:       { padding: '8px 16px', background: '#C9A84C', border: 'none', color: '#000', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
